/**
 * Modal creates popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Parent>
 *   <Modal id="UniqueIdForThisModal" isOpen={this.state.modalIsOpen} onClose={handleClose}>
 *     <FormX />
 *   </Modal>
 * </Parent>
 */
import { useRef, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '../Button/Button';
import IconClose from '../IconClose/IconClose';
import css from './Modal.module.css';

const KEY_CODE_ESCAPE = 27;

// Portal functional component (already used below)
function Portal({ children, portalRoot }) {
  const elRef = useRef(null);
  if (!elRef.current) {
    elRef.current = document.createElement('div');
  }

  useEffect(() => {
    portalRoot.appendChild(elRef.current);
    return () => {
      portalRoot.removeChild(elRef.current);
    };
  }, [portalRoot]);

  return ReactDOM.createPortal(children, elRef.current);
}

function ModalComponent(props) {
  const {
    children = null,
    className = null,
    scrollLayerClassName = null,
    closeButtonMessage = null,
    containerClassName = null,
    contentClassName = null,
    lightCloseButton = false,
    isClosedClassName = css.isClosed,
    isOpen = false,
    usePortal = false,
    id,
    onClose,
    onManageDisableScrolling,
  } = props;

  const refDiv = useRef(null);
  const [portalRoot, setPortalRoot] = useState(null);

  // Set up event listeners and portal root
  useEffect(() => {
    onManageDisableScrolling(id, isOpen);
    const handleBodyKeyUp = event => {
      if (event.keyCode === KEY_CODE_ESCAPE && isOpen) {
        handleClose(event);
      }
    };
    window.document.body.addEventListener('keyup', handleBodyKeyUp);

    // Mobile Safari hack for viewport height
    const newVh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${newVh}px`);
    const handleResize = () => {
      const updatedVh = window.innerHeight * 0.01;
      window.document.documentElement.style.setProperty('--vh', `${updatedVh}px`);
    };
    window.addEventListener('resize', handleResize);

    setPortalRoot(document.getElementById('portal-root'));

    return () => {
      window.document.body.removeEventListener('keyup', handleBodyKeyUp);
      window.removeEventListener('resize', handleResize);
      onManageDisableScrolling(id, false);
    };
  }, [id, isOpen, onManageDisableScrolling]);

  // Focus management for portal
  useEffect(() => {
    if (usePortal && isOpen && refDiv.current) {
      refDiv.current.focus();
    }
  }, [usePortal, isOpen]);

  const handleClose = useCallback(
    event => {
      onManageDisableScrolling(id, false);
      onClose(event);
    },
    [id, onClose, onManageDisableScrolling]
  );

  const closeButtonClasses = classNames(css.close, {
    [css.closeLight]: lightCloseButton,
  });
  const closeBtn = isOpen ? (
    <Button
      onClick={handleClose}
      rootClassName={closeButtonClasses}
      title={closeButtonMessage || 'Close modal'}
    >
      <span className={css.closeText}>{closeButtonMessage || 'Close'}</span>
      <IconClose rootClassName={css.closeIcon} />
    </Button>
  ) : null;

  const isOpenClass = usePortal ? css.isOpenInPortal : css.isOpenInPlace;
  const modalClass = isOpen ? isOpenClass : isClosedClassName;
  const classes = classNames(modalClass, className);
  const scrollLayerClasses = scrollLayerClassName || css.scrollLayer;
  const containerClasses = containerClassName || css.container;

  return !usePortal ? (
    <div className={classes}>
      <div className={scrollLayerClasses}>
        <div className={containerClasses}>
          {closeBtn}
          <div className={classNames(contentClassName || css.content)}>{children}</div>
        </div>
      </div>
    </div>
  ) : portalRoot ? (
    <Portal portalRoot={portalRoot}>
      <div className={classes}>
        <div className={scrollLayerClasses}>
          <div className={classNames(containerClasses, css.focusedDiv)} ref={refDiv} tabIndex="-1">
            {closeBtn}
            <div className={classNames(contentClassName || css.content)}>{children}</div>
          </div>
        </div>
      </div>
    </Portal>
  ) : null;
}

const { bool, func, node, string } = PropTypes;

ModalComponent.propTypes = {
  children: node,
  className: string,
  scrollLayerClassName: string,
  closeButtonMessage: node,
  containerClassName: string,
  contentClassName: string,
  lightCloseButton: bool,
  id: string.isRequired,
  isClosedClassName: string,
  isOpen: bool,
  onClose: func.isRequired,
  usePortal: bool,

  onManageDisableScrolling: func.isRequired,
};

export default ModalComponent;
